/* =====================================================================
 * ThemeSection — 설정 > 내 프로필 > 테마 (라이트/다크 모드 + 배경화면)
 * ===================================================================== */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Sun, Moon, Upload, Trash2, ImageIcon, ImageOff, Check } from 'lucide-react';
import {
  createSystemWallpaper,
  getSystemWallpaperAssetSrc,
  resolveEffectiveWallpaperConfig,
  useUIStore,
  type WallpaperSource,
} from '@auw/stores';
import { Checkbox } from '../../common';
import { useAdminDashboardAccess } from '../../adminDashboard/useAdminDashboardAccess';
import { SectionBlock, SectionHeader, SectionTitle, Divider } from '../styles';
import type { WorkspaceMode } from '@auw/workspace-utils';
import { useFeatureFlags } from '@auw/feature-flags';
import { WORKSPACE_MODES, WORKSPACE_ORDER } from '@auw/workspace-utils';
import { resolveSpecializedAgents, useTenant } from '@auw/tenant';
import { specializedWallpaperPageId } from '@auw/stores';

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

/*
 * 배경화면 "적용 페이지" 후보 목록.
 *
 * 메인 사이드 패널에서 노출 가능한 페이지만 포함한다.
 * 제외 대상:
 *   - `admin-page`   (팀/앱 관리) — 관리자 전용 화면
 *   - `app-manage`   (앱 관리) — 사용자 요구로 노출 금지
 *   - `skill-create` (스킬 만들기) — 사용자 요구로 노출 금지
 *
 * 실제 표시 여부는 `useFeatureFlags().isEnabled('mode.*')` 로 런타임에
 * 결정된다(Phase 4c). 테넌트 매니페스트에서 비활성화된 모드는 자동으로
 * 이 목록에서 빠진다.
 *
 * `admin-dashboard` 는 플래그만으로 판정할 수 없다 — 노출 여부를 서버의
 * 부서코드 허용목록이 정하므로 아래 `useAdminDashboardAccess()` 로 한 번 더
 * 걸러 어드민 계정에만 남긴다. 사이드바 "관리" 섹션과 같은 판정이다.
 */
const WALLPAPER_PAGE_CANDIDATES: WorkspaceMode[] = [
  ...WORKSPACE_ORDER,
  'widget',
  'feed',
  'tools',
  'message',
  'storage',
  'plugin',
  'dashboard',
  'memory',
  'skill-marketplace',
  'admin-dashboard',
];

const SYSTEM_WALLPAPER_OPTIONS = [
  {
    id: 'system-dark',
    labelKey: 'settings:theme.systemDark',
    descKey: 'settings:theme.systemDarkDesc',
    previewSrc: getSystemWallpaperAssetSrc('system-dark'),
  },
  {
    id: 'system-light',
    labelKey: 'settings:theme.systemLight',
    descKey: 'settings:theme.systemLightDesc',
    previewSrc: getSystemWallpaperAssetSrc('system-light'),
  },
] as const satisfies Array<{
  id: Exclude<WallpaperSource, 'custom'>;
  labelKey: string;
  descKey: string;
  previewSrc: string;
}>;

const DEFAULT_SELECTED_WALLPAPER_OPACITY = 100;

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const compressImage = (
  file: File,
  maxW = 1920,
  maxH = 1200,
  quality = 0.7,
): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = document.createElement('img');
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.naturalWidth;
        let h = img.naturalHeight;
        if (w > maxW) { h = (h * maxW) / w; w = maxW; }
        if (h > maxH) { w = (w * maxH) / h; h = maxH; }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('no context'));
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/* ------------------------------------------------------------------ */
/* Styled Components                                                   */
/* ------------------------------------------------------------------ */

const SubLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 10px;
`;

/* ---- Mode cards ---- */

const ModeRow = styled.div`
  display: flex;
  gap: 10px;
`;

const ModeCard = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 14px 12px 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  border: 2px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.accent : theme.colors.borderLight};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.accentLight : 'transparent'};

  &:hover {
    border-color: ${({ $active, theme }) =>
      $active ? theme.colors.accent : theme.colors.border};
    background: ${({ $active, theme }) =>
      $active ? theme.colors.accentLight : theme.colors.bgHover};
  }
`;

const ModePreview = styled.div<{ $dark: boolean }>`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 6px;
  background: ${({ $dark }) => ($dark ? '#1a1a1a' : '#f5f5f5')};
  display: flex;
  overflow: hidden;
  border: 1px solid ${({ $dark }) => ($dark ? '#333' : '#ddd')};
`;

const MiniSidebar = styled.div<{ $dark: boolean }>`
  width: 24%;
  background: ${({ $dark }) => ($dark ? '#111' : '#eaeaea')};
  padding: 6px 4px;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const MiniSidebarLine = styled.div<{ $dark: boolean; $w?: string }>`
  height: 2px;
  width: ${({ $w }) => $w || '70%'};
  border-radius: 1px;
  background: ${({ $dark }) => ($dark ? '#333' : '#ccc')};
`;

const MiniContent = styled.div<{ $dark: boolean }>`
  flex: 1;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  justify-content: center;
`;

const MiniLine = styled.div<{ $dark: boolean; $w?: string }>`
  height: 2px;
  width: ${({ $w }) => $w || '60%'};
  border-radius: 1px;
  background: ${({ $dark }) => ($dark ? '#444' : '#bbb')};
`;

const MiniBlock = styled.div<{ $dark: boolean }>`
  width: 80%;
  height: 14px;
  border-radius: 3px;
  background: ${({ $dark }) => ($dark ? '#2a2a2a' : '#e4e4e4')};
  margin-top: 2px;
`;

const ModeLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ModeIconSmall = styled.span<{ $active: boolean }>`
  display: inline-flex;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.textPrimary : theme.colors.textMuted};
  transition: color ${({ theme }) => theme.transitions.fast};
`;

const ModeName = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

/* ---- Wallpaper ---- */

const WallpaperPreviewWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 21 / 9;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};

  &:hover > div:last-child {
    opacity: 1;
  }
`;

const WallpaperPreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const WallpaperOverlayBar = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  opacity: 0;
  transition: opacity ${({ theme }) => theme.transitions.fast};
`;

const WallpaperBtn = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 14px;
  border-radius: 999px;
  transition: all ${({ theme }) => theme.transitions.fast};
  color: #fff;
  background: ${({ $danger }) =>
    $danger ? 'rgba(239,68,68,0.7)' : 'rgba(255,255,255,0.18)'};
  border: 1px solid rgba(255, 255, 255, 0.25);

  &:hover {
    background: ${({ $danger }) =>
      $danger ? 'rgba(239,68,68,0.9)' : 'rgba(255,255,255,0.32)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    flex-shrink: 0;
  }
`;

const UploadArea = styled.button`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 20px 16px;
  border-radius: 10px;
  border: 2px dashed ${({ theme }) => theme.colors.borderLight};
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const UploadText = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
`;

const UploadHint = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SystemWallpaperGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const NoneWallpaperPreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 16 / 10;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SystemWallpaperCard = styled.div<{ $active: boolean }>`
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid ${({ $active, theme }) =>
    $active ? theme.colors.accent : theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgTertiary};
  box-shadow: ${({ $active }) =>
    $active ? '0 14px 30px rgba(249, 115, 22, 0.18)' : 'none'};
  cursor: ${({ $active }) => ($active ? 'default' : 'pointer')};
  transition:
    border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast},
    transform ${({ theme }) => theme.transitions.fast};

  &:hover {
    transform: translateY(-1px);
  }

  &:hover > div:last-child {
    opacity: 1;
  }
`;

const SystemWallpaperPreview = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
`;

const SystemWallpaperMeta = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 12px;
`;

const SystemWallpaperText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const SystemWallpaperLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const SystemWallpaperDesc = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SystemWallpaperActiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(249, 115, 22, 0.12);
  color: ${({ theme }) => theme.colors.brand};
  font-size: 0.6875rem;
  font-weight: 600;
  flex-shrink: 0;
`;

/* ---- Opacity ---- */

const OpacityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OpacitySlider = styled.input`
  flex: 1;
  height: 4px;
  -webkit-appearance: none;
  appearance: none;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'};
  border-radius: 2px;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #ffffff;
    cursor: pointer;
    border: none;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2), 0 0 0 0.5px rgba(0, 0, 0, 0.08);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #ffffff;
    cursor: pointer;
    border: none;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2), 0 0 0 0.5px rgba(0, 0, 0, 0.08);
  }
`;

const OpacityValue = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 32px;
  text-align: right;
`;

/* ---- Page grid ---- */

const PageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px 12px;
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const ThemeSection: React.FC = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  const tenant = useTenant();
  /* P11-5c: tenants without `settings.appearance.wallpaper` keep only the
   * light/dark mode toggle in this section. The wallpaper picker, system
   * presets, opacity slider and per-page selector are all hidden. */
  const wallpaperPickerEnabled = isFeatureEnabled('settings.appearance.wallpaper');
  const { canView: canViewAdminDashboard } = useAdminDashboardAccess();
  const wallpaperPageOrder = useMemo<WorkspaceMode[]>(
    () => WALLPAPER_PAGE_CANDIDATES.filter((mode) => {
      if (!isFeatureEnabled(`mode.${mode}` as const)) return false;
      if (mode === 'admin-dashboard') return canViewAdminDashboard;
      return true;
    }),
    [canViewAdminDashboard, isFeatureEnabled],
  );
  /*
   * WORKSPACE_MODES[id].labelKey 는 `nav.agent` 같은 **common** 네임스페이스 키다.
   * useTranslation 의 기본 네임스페이스가 `settings` 이므로 그대로 t() 에 넘기면
   * settings.nav.agent 를 찾지 못해 `nav.agent` 문자열이 그대로 표시되는 버그가
   * 있었다. `common:` 접두를 명시해 메인 사이드 패널과 동일한 라벨이 나오게 한다.
   *
   * 추가로 매니페스트의 `specializedAgents` (예: CAD 에이전트) 도 동일한
   * 페이지 옵션 그리드에 함께 노출. WorkspaceMode 와 충돌하지 않도록
   * `specialized:<id>` prefix 로 wallpaper.pages 에 저장된다.
   */
  const specializedAgents = useMemo(
    () => resolveSpecializedAgents(tenant),
    [tenant],
  );
  const wallpaperPages = useMemo(
    () => [
      ...wallpaperPageOrder.map((id) => ({
        id: id as string,
        label: t(`common:${WORKSPACE_MODES[id].labelKey}`),
      })),
      ...specializedAgents.map((agent) => ({
        id: specializedWallpaperPageId(agent.id),
        label: agent.labelKey ? t(agent.labelKey) : agent.label,
      })),
    ],
    [t, wallpaperPageOrder, specializedAgents],
  );
  const darkMode = useUIStore((s) => s.darkMode);
  const setDarkMode = useUIStore((s) => s.setDarkMode);
  const wallpaper = useUIStore((s) => s.wallpaper);
  const wallpaperExplicit = useUIStore((s) => s.wallpaperExplicit);
  const setWallpaper = useUIStore((s) => s.setWallpaper);
  const updateWallpaper = useUIStore((s) => s.updateWallpaper);

  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const effectiveWallpaper = useMemo(
    () => resolveEffectiveWallpaperConfig({
      wallpaper,
      wallpaperExplicit,
      darkMode,
    }),
    [darkMode, wallpaper, wallpaperExplicit],
  );
  const activeSystemSource =
    effectiveWallpaper?.source === 'system-dark' || effectiveWallpaper?.source === 'system-light'
      ? effectiveWallpaper.source
      : null;
  const noneWallpaperActive = !effectiveWallpaper;

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';
      setUploading(true);
      try {
        const compressed = await compressImage(file);
        setWallpaper({
          imageData: compressed,
          opacity: DEFAULT_SELECTED_WALLPAPER_OPACITY,
          pages: effectiveWallpaper?.pages ?? [],
          applyToAll: effectiveWallpaper?.applyToAll ?? true,
          source: 'custom',
        });
      } catch {
        /* silent */
      } finally {
        setUploading(false);
      }
    },
    [effectiveWallpaper, setWallpaper],
  );

  const handleRemoveWallpaper = useCallback(() => {
    setWallpaper(null);
  }, [setWallpaper]);

  const handleApplySystemWallpaper = useCallback(
    (source: Exclude<WallpaperSource, 'custom'>) => {
      setWallpaper(createSystemWallpaper(source, {
        opacity: DEFAULT_SELECTED_WALLPAPER_OPACITY,
        pages: effectiveWallpaper?.pages ?? [],
        applyToAll: effectiveWallpaper?.applyToAll ?? true,
      }));
    },
    [effectiveWallpaper, setWallpaper],
  );

  const handleOpacityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateWallpaper({ opacity: Number(e.target.value) });
    },
    [updateWallpaper],
  );

  /* ---- page selection ---- */
  const allPagesSelected = Boolean(effectiveWallpaper?.applyToAll);

  const handleToggleAllPages = useCallback(() => {
    updateWallpaper({
      applyToAll: !allPagesSelected,
      pages: [],
    });
  }, [allPagesSelected, updateWallpaper]);

  const handleTogglePage = useCallback(
    (pageId: string) => {
      if (!effectiveWallpaper) return;
      const allIds = wallpaperPages.map((p) => p.id);
      const current =
        effectiveWallpaper.applyToAll
          ? [...allIds]
          : [...effectiveWallpaper.pages];

      const idx = current.indexOf(pageId);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(pageId);
      }
      const isAll = current.length >= allIds.length;
      updateWallpaper({
        applyToAll: isAll,
        pages: isAll ? [] : current,
      });
    },
    [effectiveWallpaper, updateWallpaper, wallpaperPages],
  );

  const isPageSelected = (pageId: string) => {
    if (!effectiveWallpaper) return false;
    return Boolean(effectiveWallpaper.applyToAll) || effectiveWallpaper.pages.includes(pageId);
  };

  return (
    <SectionBlock>
      <SectionHeader>
        <SectionTitle>{t('settings:theme.title')}</SectionTitle>
      </SectionHeader>
      <Divider />

      {/* ---- 화면 모드 ---- */}
      <SubLabel>{t('settings:theme.displayMode')}</SubLabel>
      <ModeRow>
        <ModeCard
          $active={!darkMode}
          onClick={() => setDarkMode(false)}
        >
          <ModePreview $dark={false}>
            <MiniSidebar $dark={false}>
              <MiniSidebarLine $dark={false} $w="50%" />
              <MiniSidebarLine $dark={false} $w="80%" />
              <MiniSidebarLine $dark={false} $w="60%" />
            </MiniSidebar>
            <MiniContent $dark={false}>
              <MiniLine $dark={false} $w="70%" />
              <MiniLine $dark={false} $w="50%" />
              <MiniBlock $dark={false} />
            </MiniContent>
          </ModePreview>
          <ModeLabelRow>
            <ModeIconSmall $active={!darkMode}>
              <Sun size={14} />
            </ModeIconSmall>
            <ModeName>{t('settings:theme.light')}</ModeName>
          </ModeLabelRow>
        </ModeCard>

        <ModeCard
          $active={darkMode}
          onClick={() => setDarkMode(true)}
        >
          <ModePreview $dark>
            <MiniSidebar $dark>
              <MiniSidebarLine $dark $w="50%" />
              <MiniSidebarLine $dark $w="80%" />
              <MiniSidebarLine $dark $w="60%" />
            </MiniSidebar>
            <MiniContent $dark>
              <MiniLine $dark $w="70%" />
              <MiniLine $dark $w="50%" />
              <MiniBlock $dark />
            </MiniContent>
          </ModePreview>
          <ModeLabelRow>
            <ModeIconSmall $active={darkMode}>
              <Moon size={14} />
            </ModeIconSmall>
            <ModeName>{t('settings:theme.dark')}</ModeName>
          </ModeLabelRow>
        </ModeCard>
      </ModeRow>

      {/* ---- 배경화면 ---- */}
      {wallpaperPickerEnabled && (
        <>
      <SubLabel style={{ marginTop: 20 }}>{t('settings:theme.wallpaper')}</SubLabel>

      {effectiveWallpaper?.imageData ? (
        <WallpaperPreviewWrap>
          <WallpaperPreviewImg src={effectiveWallpaper.imageData} alt="wallpaper" />
          <WallpaperOverlayBar>
            <WallpaperBtn
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              <Upload size={13} />
              {t('settings:theme.change')}
            </WallpaperBtn>
            <WallpaperBtn $danger onClick={handleRemoveWallpaper}>
              <Trash2 size={13} />
              {t('common:button.delete')}
            </WallpaperBtn>
          </WallpaperOverlayBar>
        </WallpaperPreviewWrap>
      ) : (
        <UploadArea onClick={() => fileRef.current?.click()}>
          <ImageIcon size={24} />
          <UploadText>{uploading ? t('settings:theme.uploading') : t('settings:theme.uploadWallpaper')}</UploadText>
          <UploadHint>{t('settings:theme.uploadHint')}</UploadHint>
        </UploadArea>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={handleFileSelect}
      />

      <SubLabel style={{ marginTop: 18 }}>{t('settings:theme.systemWallpaper')}</SubLabel>
      <SystemWallpaperGrid>
        <SystemWallpaperCard
          $active={noneWallpaperActive}
          onClick={noneWallpaperActive ? undefined : handleRemoveWallpaper}
        >
          <NoneWallpaperPreview>
            <ImageOff size={22} />
          </NoneWallpaperPreview>
          <SystemWallpaperMeta>
            <SystemWallpaperText>
              <SystemWallpaperLabel>{t('settings:theme.none', { defaultValue: '없음' })}</SystemWallpaperLabel>
              <SystemWallpaperDesc>{t('settings:theme.noneDesc', { defaultValue: '배경화면을 사용하지 않습니다' })}</SystemWallpaperDesc>
            </SystemWallpaperText>
            {noneWallpaperActive && (
              <SystemWallpaperActiveBadge>
                <Check size={12} />
                {t('settings:theme.inUse')}
              </SystemWallpaperActiveBadge>
            )}
          </SystemWallpaperMeta>
          {!noneWallpaperActive && (
            <WallpaperOverlayBar>
              <WallpaperBtn
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveWallpaper();
                }}
              >
                <ImageOff size={13} />
                {t('settings:theme.apply')}
              </WallpaperBtn>
            </WallpaperOverlayBar>
          )}
        </SystemWallpaperCard>
        {SYSTEM_WALLPAPER_OPTIONS.map((option) => {
          const active = activeSystemSource === option.id;
          return (
            <SystemWallpaperCard
              key={option.id}
              $active={active}
              onClick={active ? undefined : () => handleApplySystemWallpaper(option.id)}
            >
              <SystemWallpaperPreview src={option.previewSrc} alt={t(option.labelKey)} />
              <SystemWallpaperMeta>
                <SystemWallpaperText>
                  <SystemWallpaperLabel>{t(option.labelKey)}</SystemWallpaperLabel>
                  <SystemWallpaperDesc>{t(option.descKey)}</SystemWallpaperDesc>
                </SystemWallpaperText>
                {active && (
                  <SystemWallpaperActiveBadge>
                    <Check size={12} />
                    {t('settings:theme.inUse')}
                  </SystemWallpaperActiveBadge>
                )}
              </SystemWallpaperMeta>
              <WallpaperOverlayBar>
                {active ? (
                  <WallpaperBtn
                    $danger
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveWallpaper();
                    }}
                  >
                    <Trash2 size={13} />
                    {t('settings:theme.remove')}
                  </WallpaperBtn>
                ) : (
                  <WallpaperBtn
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApplySystemWallpaper(option.id);
                    }}
                  >
                    <Upload size={13} />
                    {t('settings:theme.apply')}
                  </WallpaperBtn>
                )}
              </WallpaperOverlayBar>
            </SystemWallpaperCard>
          );
        })}
      </SystemWallpaperGrid>

      {/* ---- 투명도 + 적용 페이지 (배경이미지가 있을 때만) ---- */}
      {effectiveWallpaper?.imageData && (
        <>
          <SubLabel style={{ marginTop: 18 }}>{t('settings:theme.opacity')}</SubLabel>
          <OpacityRow>
            <OpacitySlider
              type="range"
              min={5}
              max={100}
              value={effectiveWallpaper.opacity}
              onChange={handleOpacityChange}
            />
            <OpacityValue>{effectiveWallpaper.opacity}%</OpacityValue>
          </OpacityRow>

          <SubLabel style={{ marginTop: 18 }}>{t('settings:theme.applyPages')}</SubLabel>
          <PageGrid>
            <Checkbox
              checked={allPagesSelected}
              onChange={handleToggleAllPages}
              size="sm"
              label={t('settings:theme.all')}
            />
            {wallpaperPages.map((page) => (
              <Checkbox
                key={page.id}
                checked={isPageSelected(page.id)}
                onChange={() => handleTogglePage(page.id)}
                size="sm"
                label={page.label}
              />
            ))}
          </PageGrid>
        </>
      )}
        </>
      )}
    </SectionBlock>
  );
};
